// What the price-list importer accepts: Excel, CSV, and photos of a price list.
// Deliberately NOT PDF — the BE refuses it by name.
//
// The list handed to `FileUploader` has to be MIME-type-first, because the
// component validates with `acceptedTypes.includes(file.type)` and `File.type`
// is always a MIME type. An extension-only list therefore rejects *every* file
// and `onUpload` never fires. The extensions below are additive: they only
// widen the browse dialog's `accept` attribute.
//
// Mirrors src/pages/reconciliation/utils/statement-files.ts. Do not "tidy" this
// into an extension list.

/** Drives validation — must match `File.type` exactly. */
export const PRICE_IMPORT_MIME_TYPES = [
  // .xls, and also what Excel-installed Windows reports for .csv
  'application/vnd.ms-excel',
  // .xlsx
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  // .csv across the other platforms
  'text/csv',
  'application/csv',
  'text/plain',
  // photographed price lists
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;

/** Only widens the browse dialog's filter; never matches `File.type`. */
export const PRICE_IMPORT_FILE_EXTENSIONS = [
  '.xls',
  '.xlsx',
  '.csv',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
] as const;

/**
 * `File.type` comes from an OS-level mapping, so a machine without Office hands
 * back an empty string for .xls/.xlsx. Accepting it keeps those uploads
 * working.
 *
 * The cost is real and worth stating: accepting the empty type means a PDF
 * renamed .xlsx also passes browser validation. The BE closes that hole by
 * sniffing magic bytes and refusing PDFs by name, so the UI must surface that
 * rejection as a first-class error rather than a generic toast.
 */
export const UNKNOWN_MIME_TYPE = '';

export const PRICE_IMPORT_ACCEPT_TYPES: string[] = [
  ...PRICE_IMPORT_MIME_TYPES,
  ...PRICE_IMPORT_FILE_EXTENSIONS,
  UNKNOWN_MIME_TYPE,
];

/** BE cap (IMPORT_MAX_FILE_BYTES). FileUploader defaults to 5MB, which is lower. */
export const PRICE_IMPORT_MAX_FILE_BYTES = 10_000_000;
